import React, { useContext } from 'react';
import { ForgeList, ForgeDivider } from '@tylertech/forge-react';
import PropTypes from 'prop-types';
import { Context } from '../../../store/Store.js';
import NavigationCostCentersDropdown from '../NavigationCostCentersDropdown/NavigationCostCentersDropdown.js';
import ExpandingNavItem from './ExpandingNavItem.js';
import NavItem from './NavItem.js';
import { useNavigationLinks } from '../navigationLinks.js';
import { BUDGET, HOME, ORGANIZATION } from '../../../static/keyWords.js';
import { getAccessCondition } from '../../reusable/reusableFunctions.js';
import Autocomplete from '../../reusable/Autocomplete/Autocomplete.js';
import { getRelevantBudgets } from '../omniBarNavigationHelpers.js';
import { REDUCER_ACTION_TYPES } from '../../../store/Reducer.js';
import { useUser } from '../../../contexts/userContext.js';

const NavDrawerContent = ({
  settingsPanelIsOpen,
  selectedOrgName,
  setActiveOrg,
  allBudgets,
  selectedBudgetObj,  
  changeBudget,
  selected = HOME,
  setSelected,
  organizationAccessIcons,
  disableBudgetSelection = false,
}) => {
  const [state, dispatch] = useContext(Context);

  const { navItems } = useNavigationLinks();
  const { user, setShowColumns } = useUser();

  const getBudgetUniqueKey = (budget) => {
    if (budget) {
      return `${budget.id.toString()} - ${budget.name} - ${budget.year}`;
    }
  };

  const handleOrganizationChange = (organization) => {
    if (state.fireUnsavedChangesDialog) {
      dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
    } else if (organization) {
      setActiveOrg(organization);
      setShowColumns({});
    }
  };

  const handleBudgetChange = (budgetUniqueKey) => {
    const selectedBudget = allBudgets.find((b) => getBudgetUniqueKey(b) === budgetUniqueKey);
    if (state.fireUnsavedChangesDialog) {
      dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
    } else if (selectedBudget) {
      changeBudget(selectedBudget);
    }
  };

  const homeNavItem = navItems.find((x) => x.title === 'Home');
  return (
    <aside style={{ display: settingsPanelIsOpen ? 'none' : 'block' }}>
      <section className="sidePadding">
        <p className="forge-typography--overline">{`${ORGANIZATION} and ${BUDGET}`}</p>
        <Autocomplete
          options={user?.organizationNames}
          value={selectedOrgName}
          onChange={handleOrganizationChange}
          label={`${ORGANIZATION} name`}
          fastForwardSelection={state?.fireUnsavedChangesDialog}
        />
        <div style={{ marginBottom: '1rem' }} />
        <Autocomplete
          sortOptions={false}
          options={getRelevantBudgets(allBudgets, user).map((b) => ({
            value: getBudgetUniqueKey(b),
            label: `${b.name} - ${b.year}`,
          }))}
          value={getBudgetUniqueKey(user?.activeBudget || selectedBudgetObj)} // we need to rethink how we are dealing with this value
          onChange={handleBudgetChange}
          label={BUDGET}
          disabled={disableBudgetSelection}
        />
      </section>
      <ForgeDivider />
      <NavigationCostCentersDropdown />
      <ForgeDivider />

      <>
        <ForgeList navlist selectedValue={selected}>
          <NavItem
            id={`${homeNavItem.title.replaceAll(' ', '-')}-nav-item`}
            itemName={homeNavItem.title}
            iconName={homeNavItem.iconName}
            to={homeNavItem.to}
            key={homeNavItem.title}
            setSelected={setSelected}
            selected={homeNavItem.title === selected}
          />
        </ForgeList>
        <ForgeDivider />
        <ForgeList navlist selectedValue={selected}>
          {navItems
            .filter((x) => x.title !== 'Home')
            .map(({ title, iconName, subItems, name, to }) => {
              return subItems ? (
                <ExpandingNavItem
                  id={`${title.replaceAll(' ', '-')}-expanding-nav-item`}
                  itemName={title}
                  iconName={iconName}
                  subItems={subItems}
                  key={title}
                  setSelected={setSelected}
                  selected={selected}
                  organizationAccessIcons={organizationAccessIcons}
                />)
                : (
                  getAccessCondition(name, organizationAccessIcons, user) && (
                    <NavItem
                      id={`${title.replaceAll(' ', '-')}-nav-item`}
                      itemName={title}
                      iconName={iconName}
                      to={to}
                      key={title}
                      setSelected={setSelected}
                      selected={title === selected}
                    />
                  )
                );
            })}
        </ForgeList>
      </>
    </aside>
  );
};

NavDrawerContent.propTypes = {
  settingsPanelIsOpen: PropTypes.bool,
  selectedOrgName: PropTypes.string,
  setActiveOrg: PropTypes.func,
  allBudgets: PropTypes.array,
  selectedBudgetObj: PropTypes.object,
  changeBudget: PropTypes.func,
  selected: PropTypes.string,
  setSelected: PropTypes.func,
  organizationAccessIcons: PropTypes.array,
  disableBudgetSelection: PropTypes.bool,
};

export default NavDrawerContent;
